import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/*
 * tailwind-merge must know the theme's own font sizes (tokens/theme.css),
 * or it reads `text-answer` as a colour and drops it beside `text-foreground`.
 */
const twMerge = extendTailwindMerge({
  extend: { theme: { text: ["display", "question", "answer"] } },
});

/** shadcn's class combiner. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * A display hint only: the backend owns normalisation (CLAUDE.md 11) and the
 * request always carries what the person typed. Used to show "+62 812 3456 789"
 * under the field as they type, so a mistyped prefix is visible before submit.
 */
export function phonePreview(input: string): string | null {
  const digits = input.replace(/[^\d+]/g, "");
  let rest: string;
  if (digits.startsWith("+62")) rest = digits.slice(3);
  else if (digits.startsWith("62")) rest = digits.slice(2);
  else if (digits.startsWith("0")) rest = digits.slice(1);
  else if (digits.startsWith("8")) rest = digits;
  else return null;
  rest = rest.replace(/\D/g, "");
  if (!rest) return null;
  const groups = [rest.slice(0, 3), rest.slice(3, 7), rest.slice(7)].filter(Boolean);
  return `+62 ${groups.join(" ")}`;
}

/** The backend's accepted length, +62 plus 8 to 13 digits (docs/api.md 37.2). */
export function looksLikeIndonesianMobile(input: string): boolean {
  const preview = phonePreview(input);
  if (!preview) return false;
  const n = preview.replace(/\D/g, "").length - 2;
  return n >= 8 && n <= 13;
}

export function isEmail(input: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim());
}

/** Storage that never throws: private windows and blocked site data return null. */
export function storage(kind: "local" | "session") {
  const get = (): Storage | null => {
    try {
      return kind === "local" ? window.localStorage : window.sessionStorage;
    } catch {
      return null;
    }
  };
  return {
    read<T>(key: string): T | null {
      try {
        const raw = get()?.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
      } catch {
        return null;
      }
    },
    write(key: string, value: unknown): void {
      try {
        get()?.setItem(key, JSON.stringify(value));
      } catch {
        /* storage full or blocked: the session lives in memory only */
      }
    },
    remove(key: string): void {
      try {
        get()?.removeItem(key);
      } catch {
        /* nothing to do */
      }
    },
  };
}

export function uuid(): string {
  return crypto.randomUUID();
}

/** Initials for an avatar: "Andi Pratama" -> "AP". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}
